import React from "react";
import { Navigate, Link, useLocation } from "react-router-dom";
import { useMiningAuth } from "../context/MiningAuthContext";
import { cn } from "../lib/utils";
import {
  LayoutDashboard,
  Users,
  HardHat,
  Trophy,
  DollarSign,
  Wallet,
  CreditCard,
  History,
  Share2,
  Zap,
  Coins,
  Wallet2,
  LogOut,
} from "lucide-react";
import {
  removeMiningAccessToken,
  removeMiningUserData,
} from "../api/miningApi";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "./ui/sidebar";

const navGroups = [
  {
    label: "Overview",
    items: [
      { name: "Dashboard", path: "/mining/dashboard", icon: LayoutDashboard },
    ],
  },
  {
    label: "Users",
    items: [
      { name: "Active Users", path: "/mining/active-users", icon: Users },
      { name: "Inactive Users", path: "/mining/inactive-users", icon: Users },
    ],
  },
  {
    label: "Mining",
    items: [
      { name: "Active Miners", path: "/mining/active-miners", icon: HardHat },
      { name: "Leaderboard", path: "/mining/leaderboard", icon: Trophy },
      { name: "Mined Tokens", path: "/mining/mined-tokens", icon: Coins },
    ],
  },
  {
    label: "Finance",
    items: [
      { name: "Income History", path: "/mining/income", icon: DollarSign },
      { name: "Wallets", path: "/mining/wallets", icon: Wallet2 },
      { name: "Wallet Statement", path: "/mining/wallet", icon: Wallet },
      { name: "Withdrawables", path: "/mining/withdrawables", icon: CreditCard },
      { name: "Withdraw History", path: "/mining/withdraw-history", icon: History },
    ],
  },
  {
    label: "Community",
    items: [
      { name: "Social", path: "/mining/social", icon: Share2 },
      { name: "Energy Cards", path: "/mining/energy-cards", icon: Zap },
    ],
  },
];

function NavItem({ item }) {
  const location = useLocation();
  const isActive = location.pathname === item.path;
  const Icon = item.icon;

  return (
    <Link
      to={item.path}
      className={cn(
        "relative flex items-center gap-3 rounded-md py-1.5 pl-3 pr-3 text-sm transition-colors",
        isActive
          ? "bg-[#1a1d23] text-foreground before:absolute before:left-0 before:top-1/2 before:h-5 before:w-px before:-translate-y-1/2 before:bg-ginox-green"
          : "text-muted-foreground hover:bg-white/[0.04] hover:text-foreground"
      )}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      {item.name}
    </Link>
  );
}

function MiningSidebar() {
  return (
    <Sidebar
      collapsible="offcanvas"
      className="border-r border-sidebar-border"
      style={{ "--sidebar-background": "10 12 16" }}
    >
      <SidebarHeader className="h-16 flex-row items-center gap-3 px-5 border-b border-sidebar-border bg-[#0a0c10]">
        <img
          src={process.env.PUBLIC_URL + "/logo.png"}
          alt="Ginox"
          className="h-8 w-8 shrink-0 object-contain"
        />
        <div>
          <span className="font-serif text-[20px] italic leading-none text-sidebar-foreground">
            Mining
          </span>
          <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/50 select-none mt-0.5">
            Admin Panel
          </p>
        </div>
      </SidebarHeader>

      <SidebarContent className="bg-[#0a0c10] px-2 pt-3 pb-4 overflow-y-auto">
        {navGroups.map((group) => (
          <SidebarGroup key={group.label} className="py-1">
            <SidebarGroupLabel className="px-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/60">{group.label}</SidebarGroupLabel>
            <SidebarGroupContent className="space-y-0.5">
              {group.items.map((item) => (
                <NavItem key={item.path} item={item} />
              ))}
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  );
}

const MiningLayout = ({ children }) => {
  const { miningUser, miningToken, loading, logoutMining } = useMiningAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-2 border-ginox-green border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!miningToken || !miningUser) {
    return <Navigate to="/mining/login" />;
  }

  const handleLogout = () => {
    removeMiningAccessToken();
    removeMiningUserData();
    logoutMining();
  };

  return (
    <SidebarProvider defaultOpen={true}>
      <MiningSidebar />
      <SidebarInset className="bg-background min-h-screen flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        <header
          className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-4 sm:h-16 sm:px-5"
          style={{ backgroundColor: "#0b0e11" }}
        >
          <SidebarTrigger className="h-9 w-9 rounded-md border border-border/70 bg-white/[0.02] text-muted-foreground transition-colors hover:bg-white/[0.05] hover:text-foreground [&_svg]:h-4 [&_svg]:w-4" />

          {/* Breadcrumb */}
          <div className="flex min-w-0 flex-1 items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
            <span className="hidden sm:inline text-muted-foreground/50">Ginox</span>
            <span className="hidden sm:inline text-muted-foreground/30">/</span>
            <span className="text-foreground">Mining Panel</span>
          </div>

          {/* Right: user + logout */}
          <div className="flex items-center gap-3">
            {miningUser && (
              <span className="hidden sm:block truncate max-w-[160px] text-xs text-muted-foreground">
                {miningUser.email || miningUser.name}
              </span>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 rounded-md border border-border/70 bg-white/[0.02] px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-white/[0.05] hover:text-foreground"
            >
              <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </header>

        <main className="flex-1">
          <div className="mx-auto max-w-[1440px] px-5 py-6 sm:px-8 sm:py-8 md:px-10 md:py-10">
            {children}
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default MiningLayout;
